import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Keeps one bad exhibit from taking the report down.
 *
 * Charts read from calculation outputs whose shape varies by function, and
 * a report is exactly the wrong place to discover that: a reviewer facing
 * a blank page cannot tell whether the valuation is broken or the drawing
 * is. So a chart that throws degrades to a visible, honest gap, and the
 * narrative and every other exhibit still render.
 */
export class ChartBoundary extends Component<
  { children: ReactNode; label?: string },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Exhibit failed to render', this.props.label, error, info.componentStack);
  }

  override render() {
    if (this.state.failed) {
      return (
        <p className="rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-4 py-6 text-center text-sm text-amber-800">
          This exhibit could not be drawn from the calculation log. The figures in the narrative are
          unaffected — they bind to the log directly.
        </p>
      );
    }
    return this.props.children;
  }
}
